"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

const Popover = PopoverPrimitive.Root;

// PopoverTrigger is always used with asChild in this codebase (Button etc.).
// We pass through so the child controls its own surface.
const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 6, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      data-slot="popover-content"
      className={cn(
        // A crisp floating surface: large radius, hairline outline, dramatic
        // elevation. Tighter padding than a dialog — it floats, it does not
        // command the screen.
        "z-[var(--z-modal)] min-w-[8rem] rounded-xl border border-outline-variant/70 bg-background p-1.5 text-foreground shadow-[var(--elev-5)] outline-none origin-[var(--radix-popper-transform-origin)]",
        // Motion: a quick decelerated pop scaled from the trigger-anchored
        // transform origin, so it reads as emerging from the control.
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1",
        "duration-[var(--duration-short-4)] data-[state=open]:ease-[var(--ease-emphasized-decel)] data-[state=closed]:ease-[var(--ease-emphasized-accel)] motion-reduce:duration-0 motion-reduce:animate-none",
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent };
